/**
 * Expense EOM — page breaking.
 *
 * The long sections (closing ledger, stock movement, price audit, purchase ledger) run onto
 * "(CONT.)" pages, each its own A4 sheet with its own page number, exactly as the Sales EOM
 * does. The sheet geometry is the Sales EOM's (sales_eom.pages.js) so the two reports print
 * alike.
 *
 * A section is a FLOW: an ordered list of items — table rows, headings, notes, boxes — each
 * with an estimated height. Consecutive rows of the same table render as one table, and a
 * table that resumes on a new page repeats its column headers. Heights are ESTIMATES in CSS
 * pixels at the 960px page width; the report then measures each page in the browser and
 * pushes items forward off any page that overflows (`budget` takes those corrections), so an
 * estimate that runs short costs one extra layout pass, never a sliced page. Under jsdom,
 * which has no layout, the estimates alone decide.
 *
 * Pure functions. No React, no DOM.
 */
import { A4_RATIO, FOOTER_RESERVE, PAGE_BUDGET } from './sales_eom.pages';

export { A4_RATIO, FOOTER_RESERVE, PAGE_BUDGET };

// Estimated against expense_eom.css at the 960px page width.
export const H = {
  header: 128, // running header + eyebrow + rule-underlined title
  ledeLine: 22.5,
  ledePad: 16,
  ledeChars: 112,
  contLede: 39, // "Continued from the previous page."
  tableHead: 40, // one-line column headers, plus the table's top margin
  tableHead2: 54, // headers that wrap to two lines
  row: 36,
  rowLine: 17, // each extra line a wrapped cell adds
  totalRow: 40,
  noteLine: 18,
  notePad: 18,
  noteChars: 125,
  boxPad: 60, // a bordered box with a heading
  boxLine: 21,
  boxChars: 108,
  subhead: 50,
  grandTotal: 92,
};

const linesOf = (text, perLine) => (text ? Math.max(1, Math.ceil(String(text).length / perLine)) : 0);

export const ledeHeight = (text) => (text ? H.ledePad + linesOf(text, H.ledeChars) * H.ledeLine : 0);
export const noteHeight = (text) => (text ? H.notePad + linesOf(text, H.noteChars) * H.noteLine : 0);
export const boxHeight = (text) => (text ? H.boxPad + linesOf(text, H.boxChars) * H.boxLine : 0);
/** A table row whose widest cell wraps: `chars` is what fits on one line of that cell. */
export const rowHeight = (text, chars) => H.row + Math.max(0, linesOf(text, chars) - 1) * H.rowLine;

/**
 * Lay a flow onto pages.
 *
 * items: [{ table?: string, height: number, keepWithNext?: boolean }]
 *   table         rows sharing a table id render as one table; the first row of a table on
 *                 any page pays for its column headers (tableHead(id))
 *   keepWithNext  never the last item on a page — a vendor's heading belongs with its
 *                 first row, the last row of a table with its total
 * Returns [{ from, to }] — one slice of `items` per page. An empty flow is one empty page.
 */
export function paginateFlow(items, { firstHeader, contHeader, budget, tableHead = () => H.tableHead }) {
  if (!items.length) return [{ from: 0, to: 0 }];
  const pages = [];
  let i = 0;
  while (i < items.length) {
    const index = pages.length;
    const cap = budget(index);
    let used = index === 0 ? firstHeader : contHeader;
    let openTable = null;
    let j = i;
    while (j < items.length) {
      const item = items[j];
      const head = item.table && item.table !== openTable ? tableHead(item.table) : 0;
      // The first item always lands, or a page that cannot hold one item never ends.
      if (j > i && used + head + item.height > cap) break;
      used += head + item.height;
      openTable = item.table || null;
      j += 1;
    }
    while (j < items.length && j - i > 1 && items[j - 1].keepWithNext) j -= 1;
    pages.push({ from: i, to: j });
    i = j;
  }
  return pages;
}
